import type { Database } from "~~/types/supabase";

const VISIBILITY_CHECK_COOLDOWN_MS = 30_000;

// Notices a session that ended while the tab was away (expired, signed out elsewhere) and sends
// the user to the login page with a word of explanation.
//
// It only *reads* the session. supabase-js refreshes the token itself (autoRefreshToken), with a
// lock shared between tabs. This used to force refreshSession() on every focus: with two tabs open
// both refreshed the same rotating token, the second got "already used", and the guard signed the
// user out, right after logging in, or while they clicked into the login form.
export function useSessionGuard() {
  if (!import.meta.client) return;

  const supabase = useSupabaseClient<Database>();
  const supabaseUser = useSupabaseUser();
  const userStore = useUserStore();
  const route = useRoute();
  const toast = useToast();
  const { t } = useI18n();

  let lastCheck = 0;
  // the public share page needs no session either
  const onAuthPage = () => route.path.startsWith("/auth/") || route.path.startsWith("/share/");

  function clearUserStore() {
    userStore.user = null;
    userStore.organization = null;
    userStore.organizations = [];
    userStore.organizationsTotal = 0;
    userStore.rights = null;
    userStore.organizationsLoaded = false;
  }

  async function checkSession(force = false) {
    if (onAuthPage()) return;
    const now = Date.now();
    if (!force && now - lastCheck < VISIBILITY_CHECK_COOLDOWN_MS) return;
    lastCheck = now;
    // getSession refreshes an expired token when it can (lock-safe); it only comes back empty when
    // the session is really gone. A network error is not a logout.
    const { data, error } = await supabase.auth.getSession();
    if (error || data.session || !userStore.user) return;
    clearUserStore();
    toast.add({
      severity: "warn",
      summary: t("session_expired_title"),
      detail: t("session_expired_detail"),
      life: 6000,
    });
    navigateTo("/auth/login");
  }

  const onVisibilityChange = () => {
    if (document.visibilityState === "visible") checkSession();
  };
  const onOnline = () => checkSession(true);

  onMounted(() => {
    checkSession(true);
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("online", onOnline);
  });

  onUnmounted(() => {
    document.removeEventListener("visibilitychange", onVisibilityChange);
    window.removeEventListener("online", onOnline);
  });

  watch(supabaseUser, (newUser, oldUser) => {
    if (oldUser && !newUser) clearUserStore();
  });
}
